import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { SectionProps } from '../../utils/SectionProps';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Button from '../elements/Button';
import { resetPassword, validateResetToken } from '../../api/auth';
import { getPasswordRules } from '../../utils/passwordValidation';

const RESET_PASSWORD_PAGE_STATES = {
  loading: 'loading',
  invalid: 'invalid',
  ready: 'ready',
  submitting: 'submitting',
  success: 'success'
};

const COPY = {
  loadingTitle: 'Checking your reset link...',
  invalidTitle: 'This reset link is invalid or has expired.',
  invalidAction: 'Request a new reset link',
  readyTitle: 'Choose a new password',
  readyDescription: 'Enter your new password below to finish resetting your account password.',
  successTitle: 'Your password has been reset successfully.',
  successAction: 'Go to login',
  submitLabel: 'Reset Password',
  genericResetError: 'Unable to reset your password. Please try again.',
  passwordRequirementsError: 'Please review the highlighted password requirements before continuing.'
};

const propTypes = {
  ...SectionProps.types
};

const defaultProps = {
  ...SectionProps.defaults
};

const getResetErrorMessage = (error) => {
  if (!error || !error.message || error.message === 'Failed to fetch') {
    return COPY.genericResetError;
  }

  return error.message;
};

const ResetPasswordForm = (props) => {
  const location = useLocation();
  // Read the token directly from the reset-password query string and keep it only in component memory.
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const [pageState, setPageState] = useState(RESET_PASSWORD_PAGE_STATES.loading);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const passwordRules = useMemo(() => (
    getPasswordRules(formData.newPassword, formData.confirmPassword)
  ), [formData.confirmPassword, formData.newPassword]);

  useEffect(() => {
    let isMounted = true;

    const validateToken = async () => {
      setPageState(RESET_PASSWORD_PAGE_STATES.loading);
      setSubmitError('');
      setFieldErrors({});

      if (!token) {
        if (isMounted) {
          setPageState(RESET_PASSWORD_PAGE_STATES.invalid);
        }
        return;
      }

      try {
        // Validate the reset link once on page load before showing the form.
        const response = await validateResetToken(token);

        if (!isMounted) {
          return;
        }

        setPageState(response && response.valid === true
          ? RESET_PASSWORD_PAGE_STATES.ready
          : RESET_PASSWORD_PAGE_STATES.invalid);
      } catch (error) {
        if (isMounted) {
          setPageState(RESET_PASSWORD_PAGE_STATES.invalid);
        }
      }
    };

    validateToken();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const {
    className,
    topOuterDivider,
    bottomOuterDivider,
    topDivider,
    bottomDivider,
    hasBgColor,
    invertColor
  } = props;

  const outerClasses = classNames(
    'signin section',
    topOuterDivider && 'has-top-divider',
    bottomOuterDivider && 'has-bottom-divider',
    hasBgColor && 'has-bg-color',
    invertColor && 'invert-color',
    className
  );

  const innerClasses = classNames(
    'signin-inner section-inner',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider'
  );

  const updateField = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));

    setFieldErrors((prevState) => ({
      ...prevState,
      [name]: ''
    }));
    setSubmitError('');
  };

  const validateForm = () => {
    const errors = {};
    const failedRule = passwordRules.find((rule) => !rule.passed);

    // Client-side validation is only for UX; the backend still enforces the real reset rules.
    if (!formData.newPassword) {
      errors.newPassword = 'New password is required.';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password.';
    }

    if (failedRule) {
      errors.newPassword = COPY.passwordRequirementsError;
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitError(errors.newPassword || errors.confirmPassword || '');
      return;
    }

    setSubmitError('');
    // Transition into the submitting state while the reset-password API call is in flight.
    setPageState(RESET_PASSWORD_PAGE_STATES.submitting);

    try {
      // Submit the token again with the new passwords so the backend can re-check link validity.
      const response = await resetPassword({
        token,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword
      });

      if (response && response.success === true) {
        setPageState(RESET_PASSWORD_PAGE_STATES.success);
        return;
      }

      setSubmitError((response && response.message) || COPY.genericResetError);
      setPageState(RESET_PASSWORD_PAGE_STATES.ready);
    } catch (error) {
      setSubmitError(getResetErrorMessage(error));
      setPageState(RESET_PASSWORD_PAGE_STATES.ready);
    }
  };

  const renderLoadingState = () => (
    <React.Fragment>
      <SectionHeader
        tag="h1"
        data={{ title: COPY.loadingTitle, paragraph: '' }}
        className="center-content"
      />
      <div className="tiles-wrap">
        <div className="tiles-item">
          <div className="tiles-item-inner">
            <p className="mb-0 text-sm center-content text-color-low">
              {COPY.loadingTitle}
            </p>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  const renderInvalidState = () => (
    <React.Fragment>
      <SectionHeader
        tag="h1"
        data={{ title: COPY.invalidTitle, paragraph: '' }}
        className="center-content"
      />
      <div className="tiles-wrap">
        <div className="tiles-item">
          <div className="tiles-item-inner center-content">
            <p className="mb-24 text-sm text-color-low">
              {COPY.invalidTitle}
            </p>
            <Button tag={Link} to="/forgotpassword" color="primary" wide>
              {COPY.invalidAction}
            </Button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  const renderSuccessState = () => (
    <React.Fragment>
      <SectionHeader
        tag="h1"
        data={{ title: COPY.successTitle, paragraph: '' }}
        className="center-content"
      />
      <div className="tiles-wrap">
        <div className="tiles-item">
          <div className="tiles-item-inner center-content">
            <p className="mb-24 text-sm text-color-low">
              {COPY.successTitle}
            </p>
            <Button tag={Link} to="/login" color="primary" wide>
              {COPY.successAction}
            </Button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  const renderReadyState = () => (
    <React.Fragment>
      <SectionHeader
        tag="h1"
        data={{ title: COPY.readyTitle, paragraph: COPY.readyDescription }}
        className="center-content"
      />
      <div className="tiles-wrap">
        <div className="tiles-item">
          <div className="tiles-item-inner">
            <form onSubmit={handleSubmit} noValidate>
              <fieldset>
                <div className="mb-12">
                  <Input
                    id="newPassword"
                    type="password"
                    name="newPassword"
                    label="New password"
                    placeholder="New password"
                    labelHidden
                    value={formData.newPassword}
                    onChange={updateField}
                    required
                    hint={fieldErrors.newPassword}
                    status={fieldErrors.newPassword ? 'error' : ''}
                  />
                </div>

                <div className="mb-12">
                  <Input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    label="Confirm new password"
                    placeholder="Confirm new password"
                    labelHidden
                    value={formData.confirmPassword}
                    onChange={updateField}
                    required
                    hint={fieldErrors.confirmPassword}
                    status={fieldErrors.confirmPassword ? 'error' : ''}
                  />
                </div>

                <div className="mb-24">
                  <ul className="list-reset text-sm">
                    {passwordRules.map((rule) => (
                      <li key={rule.key} className={classNames('mb-8', rule.passed ? 'text-color-success' : 'text-color-error')}>
                        <span className="mr-8">{rule.passed ? '✓' : '×'}</span>
                        {rule.label}
                      </li>
                    ))}
                  </ul>
                </div>

                {submitError && (
                  <div className="mb-16 text-sm text-color-error">
                    {submitError}
                  </div>
                )}

                <div className="mt-24 mb-32">
                  <Button
                    color="primary"
                    wide
                    type="submit"
                    loading={pageState === RESET_PASSWORD_PAGE_STATES.submitting}
                    disabled={pageState === RESET_PASSWORD_PAGE_STATES.submitting}
                  >
                    {COPY.submitLabel}
                  </Button>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  const renderContent = () => {
    // Page state controls which view is safe to show: loading, invalid, form, submitting, or success.
    if (pageState === RESET_PASSWORD_PAGE_STATES.loading) {
      return renderLoadingState();
    }

    if (pageState === RESET_PASSWORD_PAGE_STATES.invalid) {
      return renderInvalidState();
    }

    if (pageState === RESET_PASSWORD_PAGE_STATES.success) {
      return renderSuccessState();
    }

    return renderReadyState();
  };

  return (
    <section className={outerClasses}>
      <div className="container">
        <div className={innerClasses}>
          {renderContent()}
        </div>
      </div>
    </section>
  );
};

ResetPasswordForm.propTypes = propTypes;
ResetPasswordForm.defaultProps = defaultProps;

export default ResetPasswordForm;
